
import { TikTokError } from "@/types/api";

export async function fetchWithTimeout(
  apiUrl: string,
  method: 'GET' | 'POST' = 'GET',
  timeout: number,
  body?: any
): Promise<any> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(apiUrl, {
      method,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: method === 'POST' ? JSON.stringify(body) : undefined,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    // Enhanced rate limit handling
    const rateLimitRemaining = response.headers.get('x-ratelimit-remaining');
    const rateLimitReset = response.headers.get('x-ratelimit-reset');
    const retryAfter = response.headers.get('retry-after');
    
    if (rateLimitRemaining === '0' || response.status === 429) {
      const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 5000;
      const resetTime = rateLimitReset ? new Date(Number(rateLimitReset) * 1000) : new Date(Date.now() + waitTime);
      console.log(`Rate limit encountered. Reset at: ${resetTime.toLocaleTimeString()}`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
      throw new Error(`Rate limit reached. Please wait until ${resetTime.toLocaleTimeString()}`);
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `API error (${response.status}): ${response.statusText}`;
      
      try {
        const errorData = JSON.parse(errorText) as TikTokError;
        if (errorData.message || errorData.error) {
          errorMessage = errorData.message || errorData.error || errorMessage;
        }
      } catch (jsonError) {
        if (errorText) {
          errorMessage = `API error: ${errorText.substring(0, 100)}${errorText.length > 100 ? '...' : ''}`;
        }
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('API response structure:', {
      keys: Object.keys(data),
      hasData: !!data.data,
      hasVideos: !!data.videos,
      hasItems: !!data.items,
      videoCount: (data.videos || data.items || []).length,
      cursor: data.cursor || data.hasMore?.cursor || "none"
    });
    
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Request timeout - the server took too long to respond');
    }
    
    throw error;
  }
}
